import { useCallback, useEffect, useRef, useState } from "react";

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const safeLocalStorage: StorageAdapter = (() => {
  if (typeof window === "undefined") {
    const mem = new Map<string, string>();
    return {
      getItem: (k) => (mem.has(k) ? mem.get(k)! : null),
      setItem: (k, v) => void mem.set(k, v),
      removeItem: (k) => void mem.delete(k),
    };
  }
  try {
    const test = "__ps_test__";
    window.localStorage.setItem(test, "1");
    window.localStorage.removeItem(test);
    return {
      getItem: (k) => window.localStorage.getItem(k),
      setItem: (k, v) => window.localStorage.setItem(k, v),
      removeItem: (k) => window.localStorage.removeItem(k),
    };
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (k) => (mem.has(k) ? mem.get(k)! : null),
      setItem: (k, v) => void mem.set(k, v),
      removeItem: (k) => void mem.delete(k),
    };
  }
})();

export type Serialize<T> = (value: T) => string;
export type Deserialize<T> = (raw: string) => T;

export interface PersistentOptions<T> {
  storage?: StorageAdapter;
  serialize?: Serialize<T>;
  deserialize?: Deserialize<T>;
  syncAcrossTabs?: boolean;
}

export function useStateWithOptionalPersistence<T>(
  key: string | null | undefined,
  initial: T | (() => T),
  options?: PersistentOptions<T>
): [
  T,
  React.Dispatch<React.SetStateAction<T>>,
  {
    destroy: () => void;
    rehydrate: () => void;
    isHydrated: boolean;
    isPersistent: boolean;
  }
] {
  const isPersistent = key != null;

  const {
    storage = safeLocalStorage,
    serialize = JSON.stringify as Serialize<T>,
    deserialize = JSON.parse as Deserialize<T>,
    syncAcrossTabs = true,
  } = options || {};

  const [isHydrated, setIsHydrated] = useState(!isPersistent);
  const initialRef = useRef(initial);

  // Initialize state based on whether we're in persistent mode
  const [state, setState] = useState<T>(() => {
    if (!isPersistent) {
      return typeof initialRef.current === "function"
        ? (initialRef.current as () => T)()
        : (initialRef.current as T);
    }

    try {
      const raw = storage.getItem(key);
      if (raw != null) return deserialize(raw);
    } catch {}

    return typeof initialRef.current === "function"
      ? (initialRef.current as () => T)()
      : (initialRef.current as T);
  });

  // Mark as hydrated for persistent mode
  useEffect(() => {
    if (isPersistent) {
      setIsHydrated(true);
    }
  }, [isPersistent]);

  // Persist state changes (only in persistent mode)
  useEffect(() => {
    if (!isPersistent) return;

    try {
      storage.setItem(key, serialize(state));
    } catch {}
  }, [key, state, storage, serialize, isPersistent]);

  // Listen for cross-tab sync (only in persistent mode)
  useEffect(() => {
    if (!isPersistent || typeof window === "undefined" || !syncAcrossTabs)
      return;

    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        if (e.newValue == null) {
          const next =
            typeof initialRef.current === "function"
              ? (initialRef.current as () => T)()
              : (initialRef.current as T);
          setState(next);
          return;
        }
        setState(deserialize(e.newValue));
      } catch {}
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, deserialize, syncAcrossTabs, isPersistent]);

  const rehydrate = useCallback(() => {
    if (!isPersistent) return; // No-op for non-persistent mode

    try {
      const raw = storage.getItem(key);
      if (raw != null) setState(deserialize(raw));
    } catch {}
  }, [key, storage, deserialize, isPersistent]);

  const destroy = useCallback(() => {
    if (!isPersistent) return; // No-op for non-persistent mode

    try {
      storage.removeItem(key);
    } catch {}
  }, [key, storage, isPersistent]);

  return [
    state,
    setState,
    {
      destroy,
      rehydrate,
      isHydrated,
      isPersistent: isPersistent as any, // TypeScript inference help
    },
  ];
}

/** Destroy a single persisted entry by key. */
export function destroyPersistentState(
  key: string,
  storage: StorageAdapter = safeLocalStorage
): void {
  try {
    storage.removeItem(key);
  } catch {
    // no-op
  }
}

/** Clear all keys within a namespace/prefix, e.g. "cart:" */
export function clearPersistentNamespace(
  prefix: string,
  storage: StorageAdapter = safeLocalStorage
): void {
  try {
    const keys: string[] = [];
    if (
      storage === safeLocalStorage &&
      typeof window !== "undefined" &&
      window.localStorage
    ) {
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(prefix)) keys.push(k);
      }
    }
    keys.forEach((k) => storage.removeItem(k));
  } catch {
    // no-op
  }
}

/* ---- Usage Examples ----

// Persistent mode (same as before)
const [count, setCount, { destroy, rehydrate, isHydrated, isPersistent }] = 
  usePersistentState("counter", 0);

// Non-persistent mode (acts like useState)
const [tempData, setTempData, { isPersistent }] = 
  usePersistentState(null, { temp: "data" });

// Conditional persistence based on props
const [formData, setFormData] = usePersistentState(
  shouldPersist ? "form-draft" : null, 
  initialFormData
);

*/
